import { useEffect, useRef } from 'react';
import { useMediaCapture } from '@features/media-capture';

/**
 * Starts the local camera/mic stream automatically when the Preview page
 * mounts (and only if it isn't already running). The Preview tab is the
 * natural home for media capture — the user always sees their own video
 * here, so we save a click and just bring it up.
 *
 * startInFlightRef guards against React StrictMode's double-invoke of
 * effects in dev: the effect can fire twice before `state` updates from
 * 'idle' to 'requesting-permissions' propagates back into this closure,
 * which would otherwise fire two concurrent getUserMedia() calls against
 * the same physical device (surfaced by Chromium as NotReadableError /
 * "device already in use").
 */
export function useAutoStartCapture(): void {
  const { state, start, settings } = useMediaCapture();
  const startInFlightRef = useRef(false);

  useEffect(() => {
    if (state === 'idle' && !startInFlightRef.current) {
      startInFlightRef.current = true;
      start(settings)
        .catch(() => {
          // Camera/mic unavailable on mount — surfaced via `error` from useMediaCapture already.
        })
        .finally(() => {
          startInFlightRef.current = false;
        });
    }
  }, [state, start, settings]);
}
